export type SortableKind = "number" | "date" | "text";

export interface INumberAffix {
  prefix: string;
  suffix: string;
}

export interface ISortableValue {
  kind: SortableKind;
  value: number | string;
  /** Only set when kind === "number" and the source text carried an affix. */
  affix?: INumberAffix;
}

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

// Affixes are digit-free; the core holds only digits, separators and spaces.
const AFFIX_RE = /^(\D*)(\d[\d.,\s]*\d|\d)(\D*)$/;

// Literal /\p{L}/u trips TS1501 under the es5 tsconfig target; the runtime supports it.
const HAS_LETTER_RE = new RegExp("\\p{L}", "u");

/**
 * Letter-bearing affixes accepted as units. Symbol-only affixes ("$", "%") need
 * no entry; this list exists so identifier-like text ("Room 5", "v2", "1st")
 * does not parse as a number. Compared case-insensitively against the trimmed affix.
 */
export const KNOWN_UNIT_AFFIXES: readonly string[] = [
  // currency codes and words
  "USD", "EUR", "GBP", "PLN", "CHF", "JPY", "CNY", "CAD", "AUD", "SEK", "NOK",
  "DKK", "CZK", "HUF", "RON", "INR", "BRL", "MXN", "ZAR", "TRY", "ILS", "KRW",
  "SGD", "HKD", "NZD", "RUB", "UAH", "zł", "kr", "Kč", "lei", "R$", "US$",
  // mass, length, area, volume
  "kg", "mg", "lb", "lbs", "oz", "km", "cm", "mm", "sqm", "ml",
  // time
  "hr", "hrs", "min", "sec", "days",
  // counts
  "pcs", "pc", "unit", "units", "bps", "ppm",
];

const KNOWN_UNIT_AFFIXES_LOWER = new Set(KNOWN_UNIT_AFFIXES.map((a) => a.toLowerCase()));

const isAcceptedAffix = (affix: string): boolean => {
  if (!HAS_LETTER_RE.test(affix)) return true;
  return KNOWN_UNIT_AFFIXES_LOWER.has(affix.trim().toLowerCase());
};

export const parseSortableValue = (text: string): ISortableValue => {
  const stripped = text.replace(/<[^>]*>/g, "").trim();
  if (stripped.length === 0) return { kind: "text", value: "" };

  const isoMatch = stripped.match(ISO_DATE_RE);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    const year = Number(y);
    const month = Number(m);
    const day = Number(d);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      const ts = Date.UTC(year, month - 1, day);
      const dt = new Date(ts);
      if (
        !Number.isNaN(ts) &&
        dt.getUTCFullYear() === year &&
        dt.getUTCMonth() === month - 1 &&
        dt.getUTCDate() === day
      ) {
        return { kind: "date", value: ts };
      }
    }
  }

  const numeric = parseAffixedNumber(stripped);
  if (numeric !== null) {
    return numeric.affix
      ? { kind: "number", value: numeric.value, affix: numeric.affix }
      : { kind: "number", value: numeric.value };
  }

  return { kind: "text", value: stripped.toLowerCase() };
};

/**
 * Split "<prefix><number><suffix>" and parse the number. Affixes keep their
 * original spacing so a result can reproduce how the author wrote the unit.
 */
export const parseAffixedNumber = (
  raw: string,
): { value: number; affix?: INumberAffix } | null => {
  let sign = 1;
  // At most one sign may apply: a leading sign, a sign glued to the digits,
  // and accounting parentheses are mutually exclusive.
  let hasSign = false;
  let body = raw.trim();
  if (body.startsWith("-")) {
    sign = -1;
    hasSign = true;
    body = body.slice(1).trimStart();
  } else if (body.startsWith("+")) {
    hasSign = true;
    body = body.slice(1).trimStart();
  }

  // Accounting negative form: "(100)" / "$(100)". The parens wrap the number
  // (with an optional prefix in front) and are not kept as affix characters.
  // The text before "(" must be digit-free, or it isn't a prefix at all
  // (e.g. "5(3)" is not "-53", it's plain text).
  const openIdx = body.indexOf("(");
  if (openIdx !== -1 && body.endsWith(")")) {
    const beforeParen = body.slice(0, openIdx);
    const inner = body.slice(openIdx + 1, -1);
    const isAccountingForm =
      !beforeParen.includes(")") &&
      !inner.includes("(") &&
      !inner.includes(")") &&
      !/\d/.test(beforeParen);
    if (isAccountingForm) {
      if (hasSign) return null;
      sign = -1;
      hasSign = true;
      body = beforeParen + inner;
    }
  }

  const match = body.match(AFFIX_RE);
  if (match === null) return null;
  let [, prefix, core, suffix] = match;

  // A sign glued directly to the digits (start of string, or right after a
  // prefix like "$") sets the sign; only the sign char leaves the prefix, so
  // "$ -100" keeps the space that isn't part of the sign.
  if (prefix.endsWith("-") || prefix.endsWith("+")) {
    if (hasSign) return null;
    sign = prefix.endsWith("-") ? -1 : sign;
    hasSign = true;
    prefix = prefix.slice(0, -1);
  }

  // A "-" or "+" may only ever occupy the sign position handled above;
  // anywhere else in the prefix or suffix the text isn't a number.
  if (prefix.includes("-") || prefix.includes("+")) return null;
  if (suffix.includes("-") || suffix.includes("+")) return null;

  // "%" only means percent when it is the entire trailing suffix; anywhere
  // else ("%50", "100%-") the text isn't a number at all.
  if (prefix.includes("%")) return null;
  if (suffix.includes("%") && suffix.trim() !== "%") return null;

  if (!isAcceptedAffix(prefix) || !isAcceptedAffix(suffix)) return null;

  const compact = compactNumericCore(core);
  if (compact === null) return null;

  const parsed = parseNumberLike(compact);
  if (parsed === null) return null;

  let value = sign * parsed;
  if (suffix.trim() === "%") value = value / 100;

  if (prefix === "" && suffix === "") return { value };
  return { value, affix: { prefix, suffix } };
};

/**
 * Whitespace inside a number is only legal as thousands grouping ("1 234 567,89").
 * Anything else is two numbers rather than one — "Q1 2024" has core "1 2024",
 * which must stay text instead of parsing as 12024.
 */
const compactNumericCore = (core: string): string | null => {
  const segments = core.trim().split(/\s+/);
  if (segments.length === 1) return segments[0];
  if (!/^\d{1,3}$/.test(segments[0])) return null;
  for (let i = 1; i < segments.length - 1; i++) {
    if (!/^\d{3}$/.test(segments[i])) return null;
  }
  if (!/^\d{3}([.,]\d+)?$/.test(segments[segments.length - 1])) return null;
  return segments.join("");
};

/** Resolve `.`/`,` as decimal vs thousands separators. Input is digits and separators only. */
const parseNumberLike = (cleaned: string): number | null => {
  if (cleaned.length === 0) return null;

  const hasComma = cleaned.includes(",");
  const hasDot = cleaned.includes(".");
  let normalized: string;

  if (hasComma && hasDot) {
    const lastComma = cleaned.lastIndexOf(",");
    const lastDot = cleaned.lastIndexOf(".");
    if (lastComma > lastDot) {
      normalized = cleaned.replace(/\./g, "").replace(",", ".");
    } else {
      normalized = cleaned.replace(/,/g, "");
    }
  } else if (hasComma) {
    const parts = cleaned.split(",");
    if (
      parts.length === 2 &&
      parts[1].length >= 1 &&
      parts[1].length <= 2 &&
      /^\d+$/.test(parts[0]) &&
      /^\d+$/.test(parts[1])
    ) {
      normalized = `${parts[0]}.${parts[1]}`;
    } else {
      normalized = cleaned.replace(/,/g, "");
    }
  } else if (hasDot) {
    const parts = cleaned.split(".");
    if (parts.length > 2) {
      const isThousands =
        parts.every((p) => /^\d+$/.test(p)) &&
        parts[0].length >= 1 &&
        parts[0].length <= 3 &&
        parts.slice(1).every((p) => p.length === 3);
      normalized = isThousands ? cleaned.replace(/\./g, "") : cleaned;
    } else {
      normalized = cleaned;
    }
  } else {
    normalized = cleaned;
  }

  if (!/^\d+(\.\d+)?$/.test(normalized)) return null;

  const n = Number(normalized);
  return Number.isNaN(n) ? null : n;
};
